import React, { type FC, type PropsWithChildren } from 'react';

import { getColor } from '@lucid/theme';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Heading from '../heading/heading';
import Container from '../container/container';
import IntegrationCard from '../cards/integration-card/integration-card';
import Text from '../text/text';

export interface IntegrationCardsProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Heading for Integration Cards section */
  sectionHeading?: string;
  /** Subheading for Integration Cards section */
  text?: string;
  /** Determines color of component background */
  backgroundColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** Determines color of sectionHeading and Subheading */
  headingColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** The cards to be shown in the section container. */
  integrationCards: Lucid.Directus.Generated.Integration_Card[];
}

const IntegrationCards: FC<PropsWithChildren<IntegrationCardsProps>> = ({
  className,
  backgroundColor,
  headingColor,
  integrationCards,
  sectionHeading,
  text,
  ...rest
}) => {
  const cards = integrationCards.filter(card => card.link?.final_url);

  return (
    <Box
      {...rest}
      as="section"
      css={theme => ({
        label: 'integrationCards',
        padding: `${theme.space.s64}px 0`,
        width: 'inherit',
        backgroundColor: getColor({
          color: backgroundColor,
          theme,
        }),
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Box css={{ maxWidth: '680px', textAlign: 'center', margin: '0px auto' }}>
        {sectionHeading && (
          <Heading
            as="h2"
            css={theme => ({
              color: getColor({
                color: headingColor,
                theme,
              }),
            })}
            variant="heading2XL"
          >
            {sectionHeading}
          </Heading>
        )}
        {text && <Text variant="paragraphL400">{text}</Text>}
      </Box>
      <Container
        css={theme => [
          {
            '.integrationCard': {
              height: '100%',
            },
            p: {
              padding: 0,
              margin: 0,
            },
            display: 'grid',
            gap: theme.space.s24,
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, auto))',
            alignItems: 'flex-start',
            marginTop: theme.space.s64,
          },
          cards.length <= 3 && {
            display: 'grid',
            gridTemplateColumns: `repeat(${cards.length}, 1fr)`,
          },
          cards.length <= 1 && {
            display: 'flex',
            justifyContent: 'center',
          },
        ]}
        extraPadding={3}
      >
        {cards.map((card, index) => (
          <IntegrationCard
            key={`integrationCard-${index + 1}`}
            icon={card.image}
            link={card.link}
            shortDescription={card.text}
            title={card.heading}
          />
        ))}
      </Container>
    </Box>
  );
};

export { IntegrationCards };
export default IntegrationCards;
